import {
  CsvDerivedColumn,
  TableVisualizationColumn,
} from "~/ui-rtk/constants/table-visualization-types"

export interface SerializableColumnConfig {
  key: string
  header: string
  type: string
  /** Percentage of two row fields instead of a field read — see `csvDerived`. */
  derived?: CsvDerivedColumn
  /** Field read when the column's own field is empty — see `csvFallbackKey`. */
  fallbackKey?: string
}

export interface CSVExportOptions {
  filename?: string
  delimiter?: string
  includeHeaders?: boolean
}

/**
 * Creates a serializable version of column configuration for web worker
 */
export function createSerializableColumnConfig(
  columns: TableVisualizationColumn[],
): SerializableColumnConfig[] {
  return columns.map(column => ({
    key: column.key,
    header: column.header || column.key,
    type: column.type || "auto",
    ...(column.csvDerived ? { derived: column.csvDerived } : {}),
    ...(column.csvFallbackKey ? { fallbackKey: column.csvFallbackKey } : {}),
  }))
}

/**
 * The value a column exports for a row: the row field under its key, or for a
 * derived column the percentage `numerator / denominator * 100` (`complement`
 * flips it to `100 - rate`). The CSV worker (public/workers/csv-export-worker.js)
 * carries the same logic for rows it fetches itself.
 */
export function resolveCellValue(
  row: Record<string, unknown>,
  column: TableVisualizationColumn | SerializableColumnConfig,
): unknown {
  const derived =
    "derived" in column
      ? column.derived
      : (column as TableVisualizationColumn).csvDerived
  if (!derived) {
    const fallbackKey =
      "fallbackKey" in column
        ? column.fallbackKey
        : (column as TableVisualizationColumn).csvFallbackKey
    const value = row[column.key]
    const isEmpty = value === null || value === undefined || value === ""
    return isEmpty && fallbackKey ? row[fallbackKey] : value
  }
  const numerator = Number(row[derived.numerator] || 0)
  const denominator = Number(row[derived.denominator] || 0)
  const rate = denominator > 0 ? (numerator / denominator) * 100 : 0
  return derived.complement ? 100 - rate : rate
}

/**
 * Formats a cell value for CSV export
 */
export function formatCellValueForCSV(
  value: unknown,
  _column: TableVisualizationColumn | SerializableColumnConfig,
): string {
  if (value === null || value === undefined) {
    return ""
  }

  // Handle different data types
  if (typeof value === "string") {
    // Escape quotes and wrap in quotes if contains delimiter, quote, or newline
    const needsQuotes =
      value.includes(",") || value.includes('"') || value.includes("\n")
    if (needsQuotes) {
      return `"${value.replace(/"/g, '""')}"`
    }
    return value
  }

  if (typeof value === "number") {
    return value.toString()
  }

  if (typeof value === "boolean") {
    return value ? "true" : "false"
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  // For complex objects, stringify them
  if (typeof value === "object") {
    try {
      const stringValue = JSON.stringify(value)
      return `"${stringValue.replace(/"/g, '""')}"`
    } catch {
      return String(value)
    }
  }

  return String(value)
}

/**
 * Converts table data to CSV format
 */
export function convertTableDataToCSV(
  data: Record<string, unknown>[],
  columnConfig: TableVisualizationColumn[] | SerializableColumnConfig[],
  options: CSVExportOptions = {},
): string {
  const { delimiter = ",", includeHeaders = true } = options

  if (!data || data.length === 0) {
    return ""
  }

  const lines: string[] = []

  // Add headers if requested
  if (includeHeaders) {
    const headers = columnConfig.map(column => {
      const header = column.header || column.key
      // Escape quotes in headers
      if (
        header.includes(delimiter) ||
        header.includes('"') ||
        header.includes("\n")
      ) {
        return `"${header.replace(/"/g, '""')}"`
      }
      return header
    })
    lines.push(headers.join(delimiter))
  }

  // Add data rows
  data.forEach(row => {
    const values = columnConfig.map(column => {
      const value = resolveCellValue(row, column)
      return formatCellValueForCSV(value, column)
    })
    lines.push(values.join(delimiter))
  })

  return lines.join("\n")
}

/**
 * Downloads CSV data as a file
 */
export function downloadCSV(
  csvContent: string,
  filename = `export-${Date.now()}.csv`,
): void {
  // Create blob with BOM for proper UTF-8 encoding in Excel
  const BOM = "\uFEFF"
  const blob = new Blob([BOM + csvContent], {
    type: "text/csv;charset=utf-8",
  })

  // Create download link
  const link = document.createElement("a")
  const url = URL.createObjectURL(blob)

  link.href = url
  link.download = filename
  link.style.display = "none"

  // Trigger download
  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Main export function that combines data conversion and download
 */
export function exportTableToCSV(
  data: Record<string, unknown>[],
  columnConfig: TableVisualizationColumn[] | SerializableColumnConfig[],
  options: CSVExportOptions = {},
): void {
  const { filename = `table-export-${Date.now()}.csv` } = options

  const csvContent = convertTableDataToCSV(data, columnConfig, options)
  downloadCSV(csvContent, filename)
}
